export type InboxProvider = {
  url: string
  providerName: string
}

type ProviderEntry = InboxProvider & { domains: readonly string[] }

// Buffer verification emails come from the buffer.com domain. Filtering on
// `from:buffer.com` is more robust than encoding a specific sender address,
// which is configured in Customer.io templates and could change.
const PROVIDERS: readonly ProviderEntry[] = [
  {
    providerName: 'Gmail',
    url: 'https://mail.google.com/mail/u/0/#search/from%3Abuffer.com',
    domains: ['gmail.com', 'googlemail.com'],
  },
  {
    providerName: 'Outlook',
    url: 'https://outlook.live.com/mail/0/inbox',
    domains: ['outlook.com', 'hotmail.com', 'live.com', 'msn.com'],
  },
  {
    providerName: 'Yahoo Mail',
    url: 'https://mail.yahoo.com/',
    domains: ['yahoo.com'],
  },
  {
    providerName: 'iCloud Mail',
    url: 'https://www.icloud.com/mail/',
    domains: ['icloud.com', 'me.com'],
  },
  {
    providerName: 'Proton Mail',
    url: 'https://mail.proton.me/',
    domains: ['proton.me', 'protonmail.com', 'pm.me'],
  },
  {
    providerName: 'Fastmail',
    url: 'https://app.fastmail.com/mail/Inbox',
    domains: ['fastmail.com'],
  },
  {
    providerName: 'AOL Mail',
    url: 'https://mail.aol.com/',
    domains: ['aol.com'],
  },
]

const PROVIDERS_BY_DOMAIN: ReadonlyMap<string, InboxProvider> = new Map(
  PROVIDERS.flatMap(({ domains, ...provider }) =>
    domains.map((domain) => [domain, provider] as const),
  ),
)

export function getInboxUrl(
  email: string | null | undefined,
): InboxProvider | null {
  if (!email) {
    return null
  }

  const atIndex = email.lastIndexOf('@')
  if (atIndex <= 0 || atIndex === email.length - 1) {
    return null
  }

  const domain = email.slice(atIndex + 1).toLowerCase()
  return PROVIDERS_BY_DOMAIN.get(domain) ?? null
}
